import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

/**
 * Global store for managing application-wide state and alerts.
 * 
 * @typedef {Object} Alert
 * @property {boolean} dismissed - Whether the alert has been dismissed
 */

/**
 * Define a Pinia store for managing global application state
 */
export const useGlobalStore = defineStore('global', () => {
    // State
    /** @type {import('vue').Ref<boolean>} */
    const isLoading = ref(false);

    /** @type {import('vue').Ref<Object.<string, Alert>>} */
    const alerts = ref({});

    // Actions
    /**
     * Set the global loading state
     * @param {boolean} status - The new loading status
     */
    function setIsLoading(status) {
        isLoading.value = status;
    }

    /**
     * Dismiss an alert and save its state to localStorage
     * @param {string} alertName - The name of the alert to dismiss
     */
    function dismissAlert(alertName) {
        alerts.value[alertName] = { dismissed: true };
        localStorage.setItem(`alert_${alertName}`, JSON.stringify({ dismissed: true }));
    }

    /**
     * Reset an alert's dismissed state
     * @param {string} alertName - The name of the alert to reset
     */
    function resetAlert(alertName) {
        alerts.value[alertName] = { dismissed: false };
        localStorage.removeItem(`alert_${alertName}`);
    }

    /**
     * Reset all alerts and clear related localStorage entries
     */
    function resetAllAlerts() {
        alerts.value = {};
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('alert_')) {
                localStorage.removeItem(key);
            }
        });
    }

    // Getters
    /**
     * Check if an alert has been dismissed
     * @type {import('vue').ComputedRef<(alertName: string) => boolean>}
     */
    const isAlertDismissed = computed(() => (alertName) => {
        const storedAlert = localStorage.getItem(`alert_${alertName}`);
        return storedAlert ? JSON.parse(storedAlert).dismissed : false;
    });

    /**
     * Check if an alert should be shown
     * @type {import('vue').ComputedRef<(alertName: string) => boolean>}
     */
    const shouldShowAlert = computed(() => (alertName) => {
        return !alerts.value[alertName]?.dismissed && !isAlertDismissed.value(alertName);
    });

    return {
        // State
        isLoading,
        alerts,

        // Actions
        setIsLoading,
        dismissAlert,
        resetAlert,
        resetAllAlerts,
        // Getters
        isAlertDismissed,
        shouldShowAlert,
    };
});